import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Box Renderer Component
const BoxRenderer = ({ box }) => {
  if (box.type === 'image') {
    const imageContent = (
      <img
        src={box.content}
        alt={box.link_text || ''}
        title={box.link_text || ''}
        className="w-full h-auto rounded-lg"
      />
    );

    if (box.link_url) {
      return (
        <a 
          href={box.link_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block hover:opacity-90 transition-opacity"
        >
          {imageContent}
        </a>
      );
    }
    return imageContent;
  }

  if (box.type === 'text') {
    return (
      <div 
        className="prose prose-sm lg:prose-base max-w-none text-[#666666]"
        dangerouslySetInnerHTML={{ __html: box.content }}
        style={{ lineHeight: '1.7' }}
      />
    );
  }

  if (box.type === 'button') {
    return (
      <a
        href={box.link_url || '#'}
        target={box.link_url?.startsWith('http') ? '_blank' : '_self'}
        rel="noopener noreferrer"
        className={`inline-flex items-center justify-center px-8 py-4 font-semibold text-sm tracking-wide uppercase transition-colors ${
          box.button_color === 'gold' 
            ? 'bg-[#D4AF37] hover:bg-[#C4A030] text-white'
            : box.button_color === 'white'
            ? 'bg-white border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white'
            : 'bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white'
        }`}
      >
        {box.button_text || 'Més informació'}
      </a>
    );
  }

  return null;
};

// Wrapper component that checks for minisite first, then falls back to original component
const MinisiteWrapper = ({ customUrl, fallbackComponent: FallbackComponent }) => {
  const [minisite, setMinisite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const fetchMinisite = async () => {
      try {
        const response = await fetch(`${API_URL}/api/minisites/public/by-url/${customUrl}`);
        if (response.ok) {
          const data = await response.json();
          setMinisite(data);
        } else {
          setUseFallback(true);
        }
      } catch (err) {
        console.error('Error fetching minisite:', err);
        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMinisite();
  }, [customUrl]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </main>
    );
  }

  // If no minisite found, use the fallback component
  if (useFallback || !minisite) {
    return <FallbackComponent />;
  }

  // Sort boxes by order
  const leftBoxes = [...(minisite.left_column || [])].sort((a, b) => a.order - b.order);
  const rightBoxes = [...(minisite.right_column || [])].sort((a, b) => a.order - b.order);

  return (
    <main data-testid="minisite-custom-page">
      <Helmet>
        <title>{minisite.seo_title || minisite.title} | UGOLF ARAVELL</title>
        {minisite.seo_description && (
          <meta name="description" content={minisite.seo_description} />
        )}
      </Helmet>

      {/* Header Image */}
      {minisite.header_image && (
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={minisite.header_image}
              alt={minisite.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </div>
        </section>
      )}

      {/* Title */}
      <section className="bg-white py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-[#1A4D2E] tracking-wide">
              {minisite.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Two Column Content */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - 2/3 */}
            <div className="lg:col-span-2 space-y-8">
              {leftBoxes.map((box, index) => (
                <motion.div
                  key={box.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <BoxRenderer box={box} />
                </motion.div>
              ))}
            </div>

            {/* Right Column - 1/3 */}
            <div className="lg:col-span-1 space-y-8">
              {rightBoxes.map((box, index) => (
                <motion.div
                  key={box.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <BoxRenderer box={box} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MinisiteWrapper;
